import { hasAFeatureFlag } from "helpers/featureHelper";
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

import { setUserFeatureFlags } from "newui/features/Auth/authSlice";
import type { FeatureFlag } from "newui/features/Auth/types/FeatureFlag.type";
import { FeatureFlagType } from "newui/features/Auth/types/FeatureFlag.type";
import type { RootState } from "newui/store";
import { useAppSelector } from "newui/store/hooks";

import { useCurrentUser } from "./auth";
import { CurrentAccountType, useProfileContext } from "newui/features/Auth/context/ProfileContext";

export const useCanAccessOnboarding = (): boolean => {
	return useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_ONBOARDING);
};

export const useCurrentUserHasDashboard = () => {
	return useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_DASHBOARD);
};

export const useCurrentUserHasRFxPreApproval = () => {
	return useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_PRE_APPROVAL_FLOW);
};

export const useHasClarificationsAccess = () => {
	const { currentProfile } = useProfileContext();
	const isFeatureFlagEnabled = hasAFeatureFlag(useCurrentUserFeatureFlags(), FeatureFlagType.ENABLE_CLARIFICATIONS);

	// feature flag is enabled OR we are in bidder mode
	return isFeatureFlagEnabled || currentProfile === CurrentAccountType.BIDDER;
}

export const useHasAIAccess = (): boolean => {
	return useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_AI);
}

export const useHasAIEvaluationAccess = (): boolean => {
	return useHasAIAccess() && useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_AI_EVALUATION);
};

export const useHasEvaluationAccess = (): boolean => {
	const hasManualEvaluation = useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_RFX_EVALUATION);
	return hasManualEvaluation;
};

export const useHasPerformanceManagementAccess = (): boolean => {
	const hasRfxFunctionality = useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_RFX_FUNCTIONALITY);
	const hasPerformanceManagement = useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_PERFORMANCE_MANAGEMENT);
	return hasRfxFunctionality && hasPerformanceManagement;
};

export const useHasRfxSettings = (): boolean => {
	const hasRfxFunctionality = useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_RFX_FUNCTIONALITY);
	const hasRfxEvaluation = useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_RFX_EVALUATION);
	const { isClientProfileActive } = useProfileContext();
	return hasRfxFunctionality && hasRfxEvaluation && isClientProfileActive;
};

export const useHasCustomiseShortlistReport = (): boolean => {
	return useCurrentUserHasFeatureFlag(FeatureFlagType.CUSTOMISE_SHORTLIST_REPORT);
};

export const useCurrentUserHasFeatureFlag = (
	featureFlag: FeatureFlagType
): boolean => {

	if (featureFlag === FeatureFlagType.ENABLE_CLARIFICATIONS) {
		return useHasClarificationsAccess();
	}

	return hasAFeatureFlag(useCurrentUserFeatureFlags(), featureFlag);
};

export const useCurrentUserFeatureFlags = (): FeatureFlagType[] => {
	const { currentUser } = useCurrentUser();
	const dispatch = useDispatch();
	const existingFeatures = useAppSelector(
		(state: RootState) => state?.auth?.user_feature_flags
	) as FeatureFlagType[] | undefined;

	// Memoize mapping of currentUser.feature_flags -> FeatureFlagType[]
	const currentUserFeatureTypes = useMemo(() => {
		return (
			currentUser?.feature_flags?.map(
				(feature: FeatureFlag) => feature.id as FeatureFlagType
			) || []
		);
	}, [currentUser?.feature_flags]);

	// Dispatch to Redux only if we have features and Redux doesn't yet
	useEffect(() => {
		if (
			currentUserFeatureTypes.length > 0 &&
			(!existingFeatures || existingFeatures.length === 0)
		) {
			dispatch(setUserFeatureFlags(currentUserFeatureTypes));
		}
	}, [currentUserFeatureTypes, existingFeatures, dispatch]);

	// Return either Redux state or fallback to currentUser features
	return existingFeatures && existingFeatures.length > 0
		? existingFeatures
		: currentUserFeatureTypes;
};
